import { useEffect, useState } from "react";
import { useUser } from "../../App";
import { AGENT_LOGIN } from "../../../shared/models";
import type { ModelAccountDTO, ModelLabDTO, ModelSignIn, ModelsPageDTO } from "../../../shared/types";
import { BrandIcon } from "../../components/BrandIcon";
import { CompanyKeyRow } from "./CompanyKey";
import { confirmDialog, Dialog, promptDialog } from "../../components/Dialog";
import { List, ListRow } from "../../components/List";
import { Switch } from "../../components/Switch";
import { TextLink } from "../../components/TextLink";
import { resetClock, WindowMeter } from "../../components/UsageMeter";
import { Avatar, Badge, Button, ButtonLink, Choice, ErrorText, Field, inputCls, PageHeader, Panel } from "../../components/ui";
import { del, get, patch, post } from "../../lib/api";

/**
 * Admin → Settings → Models (AIT-64): one block per lab — the program that runs its models and its version on the
 * workers, the models agents are offered, and what pays for a run: people's own logins, subscriptions, the company key.
 * A subscription is added with + Add, signed in through its vendor's own program on the vendor's own pages, and paced
 * by its cap; pause holds new starts and sign out removes the login from the worker. The platform keeps no secret of it.
 */

const VENDOR_WORD = { anthropic: "Claude", openai: "ChatGPT" } as const;

/** A subscription's state in a word, and the tone it reads in. */
function stateOf(a: ModelAccountDTO): { word: string; tone: "emerald" | "amber" | "red" | "zinc" | "blue" } {
  if (a.status === "signed_out") return { word: a.signIn ? "Signing in" : "Signed out", tone: a.signIn ? "blue" : "zinc" };
  if (a.status === "refused") return { word: "Sign in again", tone: "red" };
  if (a.paused) return { word: "Paused", tone: "amber" };
  if (a.status === "spent") return { word: a.spentUntil ? `Spent until ${resetClock(a.spentUntil)}` : "Spent", tone: "amber" };
  if (a.renewing) return { word: "Renewing", tone: "blue" };
  return { word: "Signed in", tone: "emerald" };
}

/** Anthropic's address and the code pasted back, or OpenAI's address and its one-time code with the page waiting. */
function SignInSteps({ account, signIn, onDone }: { account: Pick<ModelAccountDTO, "id" | "vendor">; signIn: ModelSignIn; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (signIn.kind === "device") {
    return (
      <div className="space-y-3 text-sm">
        <p>
          1. Open <TextLink href={signIn.url}>{signIn.url}</TextLink> and sign in to the ChatGPT account of this {AGENT_LOGIN.one}.
        </p>
        <div>
          <p>2. Enter this one-time code there:</p>
          <p className="mt-1 font-mono text-title font-semibold tracking-widest">{signIn.code}</p>
          <p className="text-xs text-faint">It works once, for 15 minutes. Never give it to anyone.</p>
        </div>
        <p className="text-xs text-muted">This page waits for OpenAI and says when the sign-in is done. A personal account needs "device code login" turned on in ChatGPT's Security settings first.</p>
      </div>
    );
  }
  return (
    <form
      className="space-y-3 text-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await post(`/api/models/accounts/${account.id}/code`, { code });
          onDone();
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <p>1. Open Anthropic's sign-in page and approve access with the Claude account of this {AGENT_LOGIN.one}.</p>
      <ButtonLink href={signIn.url}>Open Anthropic's sign-in ↗</ButtonLink>
      <Field label="2. The code Anthropic shows you" hint="Paste it within 15 minutes.">
        <input className={`${inputCls} font-mono`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="paste the code here" autoComplete="off" />
      </Field>
      <Button variant="primary" disabled={busy || !code.trim()}>
        {busy ? "Signing in…" : "Submit code"}
      </Button>
      <ErrorText error={error} />
    </form>
  );
}

/** + Add: the vendor and a label, then the vendor's own sign-in, the dialog following it to the end. */
function AddSubscription({ vendor: initial, page, onClose, onChanged }: { vendor: "anthropic" | "openai"; page: ModelsPageDTO; onClose: () => void; onChanged: () => void }) {
  const [vendor, setVendor] = useState(initial);
  const [label, setLabel] = useState("");
  const [started, setStarted] = useState<{ account: ModelAccountDTO; signIn: ModelSignIn } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const now = started ? page.labs.flatMap((l) => l.subscriptions).find((a) => a.id === started.account.id) : null;
  const done = now?.status === "signed_in";
  const close = async () => {
    // Left half-way: the program waiting for the person is ended, and nothing was signed in.
    if (started && !done) await post(`/api/models/accounts/${started.account.id}/cancel`).catch(() => {});
    onChanged();
    onClose();
  };
  return (
    <Dialog
      open
      onClose={close}
      title={started ? `Sign in ${started.account.label}` : AGENT_LOGIN.add}
      description={started ? "Through the vendor's own program, on the vendor's own pages. AgentOS keeps no password and no token: only that it is signed in, and as whom." : "Agents' own runs speak with it. It is yours: only you sign it in, and an agent's list may name it while you own that agent."}
      busy={busy}
      cancel={done ? false : started ? "Cancel sign-in" : undefined}
      primary={
        done ? (
          <Button variant="primary" onClick={() => { onChanged(); onClose(); }}>Done</Button>
        ) : started ? undefined : (
          <Button
            variant="primary"
            disabled={busy || !page.signInReady}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setStarted(await post<{ account: ModelAccountDTO; signIn: ModelSignIn }>("/api/models/accounts", { vendor, label: label.trim() || undefined }));
                onChanged();
              } catch (err) {
                setError((err as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Starting…" : "Sign in"}
          </Button>
        )
      }
    >
      {done ? (
        <p className="text-sm">Signed in{now?.account ? <> as <span className="font-medium">{now.account}</span></> : null}. Its cap is {now?.cap} runs at once; add it to an agent's model list in the agent's settings.</p>
      ) : started ? (
        <SignInSteps account={started.account} signIn={started.signIn} onDone={onChanged} />
      ) : (
        <div className="space-y-4">
          <Choice label="Vendor" value={vendor} onChange={setVendor} options={[{ value: "anthropic", label: "Anthropic (Claude)" }, { value: "openai", label: "OpenAI (ChatGPT)" }]} />
          <Field label="Label" hint="How it is named on this page and in agents' settings.">
            <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`My ${VENDOR_WORD[vendor]} plan`} maxLength={80} />
          </Field>
          {!page.signInReady && <p className="text-sm text-warning">The worker for agents' own runs is not connected: {AGENT_LOGIN.many} are signed in there. Try again in a minute.</p>}
        </div>
      )}
      <div className="mt-3">
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

function SubscriptionRow({ account: a, onChanged, onError }: { account: ModelAccountDTO; onChanged: () => void; onError: (e: string | null) => void }) {
  const state = stateOf(a);
  const act = async (fn: () => Promise<unknown>) => {
    onError(null);
    try {
      await fn();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      onChanged();
    }
  };
  const signedIn = a.status === "signed_in" || a.status === "spent";
  const me = useUser();
  return (
    <ListRow
      leading={<Avatar name={a.ownerName} size="sm" />}
      title={
        <>
          <span className="font-medium">{a.label}</span>
          <Badge tone={state.tone}>{state.word}</Badge>
        </>
      }
      subtitle={`${a.ownerName}'s${a.account ? ` · signed in as ${a.account}` : ""}`}
      trailing={
        <>
          <span className="tabular-nums">{a.running} of {a.cap} running</span>
          <Button
            size="sm"
            onClick={() =>
              act(async () => {
                const value = await promptDialog({ title: `Cap of ${a.label}`, body: "How many of agents' own runs it takes at once; the rest wait in the queue.", label: "Runs at once", value: String(a.cap), confirm: "Save cap" });
                if (value === null) return;
                await patch(`/api/models/accounts/${a.id}`, { cap: Number(value) });
              })
            }
          >
            Cap {a.cap}
          </Button>
          {a.status !== "signed_out" && <Switch size="sm" label="Paused" checked={a.paused} onChange={(paused) => act(() => patch(`/api/models/accounts/${a.id}`, { paused }))} />}
          {a.vendor === "openai" && a.status === "signed_in" && a.ownerUserId === me.id && (
            <Button size="sm" disabled={a.renewing} onClick={() => act(() => post(`/api/models/accounts/${a.id}/renew`))}>
              {a.renewing ? "Renewing…" : "Renew now"}
            </Button>
          )}
          {signedIn || a.status === "refused" ? (
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                act(async () => {
                  if (!(await confirmDialog({ title: `Sign ${a.label} out?`, body: "Its login is removed from the worker. Runs going now finish; agents whose list names it wait or take their next model.", confirm: "Sign out", danger: true }))) return;
                  await post(`/api/models/accounts/${a.id}/signout`);
                })
              }
            >
              Sign out
            </Button>
          ) : a.signIn ? (
            <Button size="sm" onClick={() => act(() => post(`/api/models/accounts/${a.id}/cancel`))}>Cancel sign-in</Button>
          ) : (
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                act(async () => {
                  if (!(await confirmDialog({ title: `Remove ${a.label}?`, body: "It is signed out; removing it takes it off this page for good.", confirm: "Remove", danger: true }))) return;
                  await del(`/api/models/accounts/${a.id}`);
                })
              }
            >
              Remove
            </Button>
          )}
        </>
      }
    >
      {(a.usage?.fiveHour || a.usage?.sevenDay) && (
        <div className="mt-2 space-y-1">
          <WindowMeter label="5-hour" window={a.usage.fiveHour} />
          <WindowMeter label="Weekly" window={a.usage.sevenDay} />
        </div>
      )}
      {a.detail && <p className="mt-1 text-xs text-muted">{a.detail}</p>}
      {a.agents.length > 0 && <p className="mt-1 text-xs text-faint">On the model list of {a.agents.map((x) => x.name).join(", ")}</p>}
      {a.signIn && (
        <div className="mt-3 rounded-xl bg-fill-subtle p-3">
          <SignInSteps account={a} signIn={a.signIn} onDone={onChanged} />
        </div>
      )}
    </ListRow>
  );
}

function LabBlock({ lab, onAdd, onChanged, onError }: { lab: ModelLabDTO; onAdd: () => void; onChanged: () => void; onError: (e: string | null) => void }) {
  const driver = lab.driver;
  const title = (
    <span className="flex flex-wrap items-center gap-2">
      <BrandIcon brand={lab.vendor} className="size-5 shrink-0" /> {lab.name}
      {driver && <Badge tone={driver.healthy ? "emerald" : "red"}>{driver.healthy ? "Healthy" : driver.detail ?? "Not answering"}</Badge>}
    </span>
  );
  if (lab.unavailable) {
    return (
      <Panel title={title} action={<Badge>Not available</Badge>}>
        <p className="text-sm text-muted">{lab.unavailable}.</p>
        <p className="mt-1 text-xs text-faint">Company key: none is used.</p>
      </Panel>
    );
  }
  return (
    <List
      title={title}
      hint={driver ? `Runs on ${driver.name}${driver.versions.length ? ` ${driver.versions.join(", ")}` : ""}` : undefined}
      action={<Button size="sm" onClick={onAdd}>{AGENT_LOGIN.add}</Button>}
    >
      <ListRow
        title={<span className="font-medium">Models offered to agents</span>}
        subtitle={`${lab.models.length} model${lab.models.length === 1 ? "" : "s"}, chosen per agent in its settings`}
      >
        <div className="mt-2 flex flex-wrap gap-1.5">
          {lab.models.map((m) => (
            <span key={m.id} title={m.description}>
              <Badge>{m.label}</Badge>
            </span>
          ))}
        </div>
      </ListRow>
      {lab.peopleLogins !== null && (
        <ListRow
          title={<span className="font-medium">People's own logins</span>}
          subtitle={`${lab.peopleLogins} ${lab.peopleLogins === 1 ? "person runs" : "people run"} their chats on a ${VENDOR_WORD[lab.vendor as "anthropic"]} of their own`}
          trailing={<TextLink to="/connections">My connections</TextLink>}
        >
          <p className="mt-0.5 text-xs text-faint">People use their own Claude, or wait until they connect one: Sign in with Claude (their plan) or their own Claude Console API key.</p>
        </ListRow>
      )}
      {lab.companyKey === null ? (
        <ListRow title={<span className="font-medium">Company key</span>} subtitle="None is used" trailing={<Badge>None</Badge>}>
          <p className="mt-0.5 text-xs text-faint">Its runs are paid by {AGENT_LOGIN.a}.</p>
        </ListRow>
      ) : (
        <CompanyKeyRow vendor={lab.vendor} companyKey={lab.companyKey} onChanged={onChanged} onError={onError} />
      )}
      {lab.subscriptions.length === 0 ? (
        <ListRow
          title={<span className="font-medium">{AGENT_LOGIN.title}</span>}
          subtitle={`None yet: agents' own runs on ${lab.name}'s models use what they used before.`}
          trailing={<TextLink onClick={onAdd}>Add one</TextLink>}
        />
      ) : (
        lab.subscriptions.map((a) => <SubscriptionRow key={a.id} account={a} onChanged={onChanged} onError={onError} />)
      )}
    </List>
  );
}

export function AdminModels() {
  const [page, setPage] = useState<ModelsPageDTO | null>(null);
  const [adding, setAdding] = useState<"anthropic" | "openai" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => get<ModelsPageDTO>("/api/models").then(setPage, (e) => setError(e.message));
  const waiting = page?.labs.some((l) => l.subscriptions.some((a) => a.signIn || a.renewing)) || adding !== null;
  useEffect(() => {
    load();
    // A device sign-in finishes at OpenAI, not here: the page looks again every few seconds while one waits.
    const t = setInterval(load, waiting ? 2_000 : 30_000);
    return () => clearInterval(t);
  }, [waiting]);
  return (
    <div>
      <PageHeader title="Models" subtitle="The labs agents run on, the program that runs each one's models, and what pays for a run." />
      <div className="max-w-4xl space-y-6 px-4 py-5 md:px-6">
        <ErrorText error={error} />
        {page?.labs.map((lab) => (
          <LabBlock key={lab.vendor} lab={lab} onAdd={() => setAdding(lab.vendor === "openai" ? "openai" : "anthropic")} onChanged={load} onError={setError} />
        ))}
        <p className="text-xs text-faint">
          {AGENT_LOGIN.title} pay only for runs with nobody behind them: an agent's wakes, its schedules that run as itself, and what those hand over. A run for a person never uses one.
        </p>
      </div>
      {adding && page && <AddSubscription vendor={adding} page={page} onClose={() => setAdding(null)} onChanged={load} />}
    </div>
  );
}
